import { AnimatePresence, motion } from 'framer-motion';
import { ChevronLeft, ChevronRight, X } from '@/components/icons';
import { useCallback, useEffect, useState } from 'react';
import { Dialog, DialogTitle } from './dialog';
import { FullscreenDialogContent } from './fullscreen-dialog';
import { ImageIndicator } from './image-indicator';

interface ImageLightboxProps {
  images: Array<{ src: string; alt: string }> | string[];
  selectedIndex: number | null;
  isOpen: boolean;
  onClose: () => void;
  onIndexChange: (index: number) => void;
  slideDirection?: 'left' | 'right';
  onSlideDirectionChange?: (direction: 'left' | 'right') => void;
}

export function ImageLightbox({
  images,
  selectedIndex,
  isOpen,
  onClose,
  onIndexChange,
  onSlideDirectionChange
}: ImageLightboxProps) {
  // 内部でアニメーション用の方向を管理
  const [internalDirection, setInternalDirection] = useState<'left' | 'right'>('right');
  
  // 画像配列を正規化
  const normalizedImages = images.map((img, index) =>
    typeof img === 'string'
      ? { src: img, alt: `Image ${index + 1}` }
      : img
  );

  // 次の画像をプリロード（モバイルパフォーマンス向上）
  useEffect(() => {
    if (selectedIndex === null || !isOpen) return;
    
    const preloadImages = [];
    
    // 次の画像をプリロード
    if (selectedIndex < normalizedImages.length - 1) {
      const nextImage = new Image();
      nextImage.src = normalizedImages[selectedIndex + 1].src;
      preloadImages.push(nextImage);
    }
    
    // 前の画像をプリロード
    if (selectedIndex > 0) {
      const prevImage = new Image();
      prevImage.src = normalizedImages[selectedIndex - 1].src;
      preloadImages.push(prevImage);
    }
    
    // ループの場合の対応
    if (selectedIndex === 0 && normalizedImages.length > 1) {
      const lastImage = new Image();
      lastImage.src = normalizedImages[normalizedImages.length - 1].src;
      preloadImages.push(lastImage);
    }
    
    if (selectedIndex === normalizedImages.length - 1 && normalizedImages.length > 1) {
      const firstImage = new Image();
      firstImage.src = normalizedImages[0].src;
      preloadImages.push(firstImage);
    }
  }, [selectedIndex, normalizedImages, isOpen]);

  const handleNext = useCallback(() => {
    if (selectedIndex === null) return;
    // 次へ進む = 新しいコンテンツが右から入る
    setInternalDirection('right');
    onSlideDirectionChange?.('right');
    // 少し遅延させてからインデックスを変更
    setTimeout(() => {
      const nextIndex = selectedIndex < normalizedImages.length - 1 ? selectedIndex + 1 : 0;
      onIndexChange(nextIndex);
    }, 10);
  }, [selectedIndex, normalizedImages.length, onIndexChange, onSlideDirectionChange]);

  const handlePrev = useCallback(() => {
    if (selectedIndex === null) return;
    // 前へ戻る = 新しいコンテンツが左から入る
    setInternalDirection('left');
    onSlideDirectionChange?.('left');
    // 少し遅延させてからインデックスを変更
    setTimeout(() => {
      const prevIndex = selectedIndex > 0 ? selectedIndex - 1 : normalizedImages.length - 1;
      onIndexChange(prevIndex);
    }, 10);
  }, [selectedIndex, normalizedImages.length, onIndexChange, onSlideDirectionChange]);

  // キーボードナビゲーション
  useEffect(() => {
    if (!isOpen || selectedIndex === null) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') {
        handlePrev();
      } else if (e.key === 'ArrowRight') {
        handleNext();
      } else if (e.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, selectedIndex, handleNext, handlePrev, onClose]);

  const handleIndicatorSelect = useCallback((index: number) => {
    if (selectedIndex !== null) {
      // インデックスが増える（進む）: 新しいコンテンツが右から入る
      // インデックスが減る（戻る）: 新しいコンテンツが左から入る
      const direction = index > selectedIndex ? 'right' : 'left';
      setInternalDirection(direction);
      onSlideDirectionChange?.(direction);
      // 少し遅延させてからインデックスを変更
      setTimeout(() => {
        onIndexChange(index);
      }, 10);
    } else {
      onIndexChange(index);
    }
  }, [selectedIndex, onIndexChange, onSlideDirectionChange]);

  if (selectedIndex === null) return null;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogTitle className="sr-only">
        {normalizedImages[selectedIndex].alt}
      </DialogTitle>
      <FullscreenDialogContent onInteractOutside={onClose}>
        {/* 前へボタン */}
        <button
          className="absolute left-4 md:left-8 bottom-24 bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-full p-3 md:p-4 transition-all z-20"
          onClick={(e) => {
            e.stopPropagation();
            handlePrev();
          }}
          aria-label="前の画像"
        >
          <ChevronLeft className="w-6 h-6 md:w-8 md:h-8 text-white" />
        </button>

        {/* 次へボタン */}
        <button
          className="absolute right-4 md:right-8 bottom-24 bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-full p-3 md:p-4 transition-all z-20"
          onClick={(e) => {
            e.stopPropagation();
            handleNext();
          }}
          aria-label="次の画像"
        >
          <ChevronRight className="w-6 h-6 md:w-8 md:h-8 text-white" />
        </button>

        {/* 閉じるボタン */}
        <button
          className="absolute bottom-4 right-4 md:bottom-8 md:right-8 bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-full p-3 transition-all z-20"
          onClick={(e) => {
            e.stopPropagation();
            onClose();
          }}
          aria-label="閉じる"
        >
          <X className="w-6 h-6 text-white" />
        </button>

        {/* スワイプ対応の画像表示 */}
        <AnimatePresence mode="wait" initial={false} custom={internalDirection}>
          <motion.img
            key={selectedIndex}
            src={normalizedImages[selectedIndex].src}
            alt={normalizedImages[selectedIndex].alt}
            className="max-w-full max-h-full object-contain select-none pointer-events-auto"
            style={{
              touchAction: 'pan-x pan-y',
              WebkitTouchCallout: 'none',
              willChange: 'transform, opacity'
            }}
            custom={internalDirection}
            variants={{
              enter: (direction: 'left' | 'right') => ({
                x: direction === 'right' ? 300 : -300,
                opacity: 0
              }),
              center: {
                x: 0,
                opacity: 1
              },
              exit: (direction: 'left' | 'right') => ({
                x: direction === 'right' ? -300 : 300,
                opacity: 0
              })
            }}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            drag="x"
            dragConstraints={{ left: 0, right: 0 }}
            dragElastic={1}
            onDragEnd={(_e, { offset, velocity }) => {
              const swipe = Math.abs(offset.x) * velocity.x;
              if (swipe < -10000) {
                handleNext();
              } else if (swipe > 10000) {
                handlePrev();
              }
            }}
          />
        </AnimatePresence>

        {/* インジケーター */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20">
          <ImageIndicator
            total={normalizedImages.length}
            current={selectedIndex}
            onSelect={handleIndicatorSelect}
            activeClassName="bg-white w-2 h-2"
            inactiveClassName="bg-white/50 hover:bg-white/70 w-2 h-2"
          />
        </div>

        {/* 画像カウンター */}
        <div className="absolute bottom-12 left-1/2 -translate-x-1/2 text-white/80 text-sm md:text-base">
          {selectedIndex + 1} / {normalizedImages.length}
        </div>
      </FullscreenDialogContent>
    </Dialog>
  );
}
